import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import sendAnalytics from '@st-operations/uikit/lib/sendAnalytics.js'
import { Button, BUTTON_THEME, CheckBox, Icon, Input } from '@st-operations/uikit/src/v2/index.js'

import Select from '_components/select'
import RadioGroup from '_components/radio-input-group'
import MultiValueInput from '_components/multi-value-input'
import UploadCSVModal from '_components/upload-csv-modal'

import { useModal } from '_hooks'

import { createMediaMeasurementCampaign } from '_services/campaigns'
import {
  COUNTRIES_OPTIONS,
  DESTINATION,
  DESTINATION_TITLES,
  LOOKBACK_OPTIONS,
  OMM_STATUS_OPTIONS,
  SCHEMA,
} from '../constants'

import { newLog } from '_services/logs'

import './styles.scss'

const STEPS = {
  ONE: '1',
  TWO: '2',
  THREE: '3',
  FOUR: '4',
}

const BASE_STATE = {
  [SCHEMA.NAME]: '',
  [SCHEMA.CAMPAIGN_OBJECTIVE]: '',
  [SCHEMA.COUNTRIES]: COUNTRIES_OPTIONS[1],
  [SCHEMA.BRAND_NAME]: '',
  [SCHEMA.PRODUCT_NAMES]: [],
  [SCHEMA.LOOKBACK]: LOOKBACK_OPTIONS[0].toLowerCase().replaceAll(' ', '_'),
  [SCHEMA.DELIVERY_DESTINATION]: DESTINATION.STATSOCIAL,
  [SCHEMA.EMAIL_NOTIFICATION]: true,
  [SCHEMA.URLS]: [],
}

const StepOne = ({ data, updateState }) => {
  return (
    <div className="media-measurement-campaign-step-one">
      <Input
        label="Audience Name"
        name={SCHEMA.NAME}
        onChange={updateState(SCHEMA.NAME)}
        value={data[SCHEMA.NAME]}
        placeholder="Ex. Nike Basketball Shoes"
      />
      <Input
        label="Campaign Objective"
        name={SCHEMA.CAMPAIGN_OBJECTIVE}
        onChange={updateState(SCHEMA.CAMPAIGN_OBJECTIVE)}
        value={data[SCHEMA.CAMPAIGN_OBJECTIVE]}
      />
      <Select
        label="Regions"
        name={SCHEMA.COUNTRIES}
        options={COUNTRIES_OPTIONS}
        onChange={updateState(SCHEMA.COUNTRIES)}
        value={data[SCHEMA.COUNTRIES]}
        useReactSelect
      />
      <Input
        label="Brand Mentions"
        name={SCHEMA.BRAND_NAME}
        onChange={updateState(SCHEMA.BRAND_NAME)}
        value={data[SCHEMA.BRAND_NAME]}
        containerStyle={{ marginTop: 20 }}
        placeholder="Ex. NIKE"
      />
      <MultiValueInput
        label="Keywords for Contextual Expansion"
        values={data[SCHEMA.PRODUCT_NAMES]}
        onChange={updateState(SCHEMA.PRODUCT_NAMES)}
      />
      <div className="media-measurement-campaign-step-two__note">
        Optional. Press enter to add keywords
      </div>
      <If condition={data[SCHEMA.BRAND_NAME] && data[SCHEMA.PRODUCT_NAMES].length}>
        <div className="media-measurement-campaign-step-two__note">
          {data[SCHEMA.PRODUCT_NAMES].map((keyword, index) => (
            <div>{`${data[SCHEMA.BRAND_NAME]} AND ${keyword} ${
              data[SCHEMA.PRODUCT_NAMES].length - 1 !== index ? 'OR' : ''
            }`}</div>
          ))}
        </div>
      </If>
      <p style={{ marginTop: 20 }}>Look Back Window</p>
      <RadioGroup
        className={'flex flex-row gap-8'}
        options={LOOKBACK_OPTIONS.map((option) => ({
          label: option,
          checked:
            data[SCHEMA.LOOKBACK].toLowerCase().replaceAll(' ', '_') ===
            option.toLowerCase().replaceAll(' ', '_'),
        }))}
        onChange={(event) =>
          updateState(SCHEMA.LOOKBACK)(event.target.value.toLowerCase().replaceAll(' ', '_'))
        }
      />
      <p>Delivery Destination</p>
      <RadioGroup
        className={'flex flex-row gap-8'}
        groupName="Destination"
        options={Object.values(DESTINATION).map((dest) => ({
          label: dest,
          checked: data[SCHEMA.DELIVERY_DESTINATION] === dest,
          disabled: !data[SCHEMA.COUNTRIES].destinationOptions.includes(dest),
          title: DESTINATION_TITLES[dest],
        }))}
        onChange={(event) => updateState(SCHEMA.DELIVERY_DESTINATION)(event.target.value)}
      />
    </div>
  )
}

const StepTwo = ({ data, updateState }) => {
  return (
    <div className="media-measurement-campaign-step-two">
      <MultiValueInput
        label="Reference URLs"
        values={data[SCHEMA.URLS]}
        onChange={updateState(SCHEMA.URLS)}
      />
      <p className="media-measurement-campaign-step-two__note">Use new lines to separate URLs</p>
      <p className="media-measurement-campaign-step-two__note">
        URLs count: {data[SCHEMA.URLS].length}
      </p>
      <CheckBox
        label="Get Email notification when processed and delivered"
        checked={data[SCHEMA.EMAIL_NOTIFICATION]}
        onChange={(e) => updateState(SCHEMA.EMAIL_NOTIFICATION)(e.target.checked)}
      />
    </div>
  )
}

// const StepThree = ({ data, updateReports }) => {
//   return (
//     <section className="media-measurement-campaign-step-three">
//       <p>Select the reports that you would like to be generated with your delivery</p>
//       <div className="media-measurement-campaign-step-three__items">
//         <CheckBox
//           label="Demographic Report"
//           checked={data?.reports?.demographic?.selected ?? false}
//           onChange={(e) => updateReports('demographic')(e.target.checked)}
//         />
//         <CheckBox
//           label="Audience Trend Report"
//           checked={data?.reports?.audienceTrend?.selected ?? false}
//           onChange={(e) => updateReports('audienceTrend')(e.target.checked)}
//         />
//       </div>
//     </section>
//   )
// }

const StepFour = () => {
  return (
    <div className="media-measurement-campaign-step-four">
      <h2>Audience Submitted!</h2>
      <p>Now we are finding the URLs...</p>
      <div className="media-measurement-campaign-step-four__steps">
        <div>
          <Icon icon="checked" type="outline" color="#3d3d3d" height="80px" width="80px" />
          <p>Submit Audience</p>
        </div>
        <div>
          <Icon icon="angleright" type="outline" color="#3d3d3d" height="28px" width="28px" />
        </div>
        <div>
          <Icon icon="search" type="outline" color="#13682B" height="80px" width="80px" />
          <p style={{ color: '#13682B', fontWeight: 600 }}>Finding URLs</p>
        </div>
        <div>
          <Icon icon="angleright" type="outline" color="#3d3d3d" height="28px" width="28px" />
        </div>
        <div>
          <Icon icon="uploadcloud" type="outline" color="#3d3d3d" height="80px" width="80px" />
          <p>Delivering</p>
        </div>
      </div>
    </div>
  )
}

export const filterKeywordBasedOnBrand = (data, update) => {
  const { brandName, productNames } = data
  const brand = brandName.toLowerCase()
  const keywords = productNames.map((keyword) => keyword.toLowerCase())
  if (keywords.includes(brand)) {
    update(productNames.filter((keyword) => keyword.toLowerCase() !== brand))
    toast(
      `"${brandName}" is already used as Brand Mention and got removed from Keywords for Contextual Expansion`
    )
  }
}

const CreateMediaMeasurementCampaignModal = ({ title, onClose, refresh, isOpen }) => {
  const [step, setStep] = useState(STEPS.ONE)

  const [data, setData] = useState(BASE_STATE)
  const updateState = (name) => (value) => setData((prev) => ({ ...prev, [name]: value }))
  const updateReports = (name) => (value) =>
    setData((prev) => ({
      ...prev,
      reports: {
        ...(prev?.reports ?? {}),
        [name]: {
          ...(prev?.reports?.[name] ?? {}),
          selected: value,
        },
      },
    }))

  useEffect(() => {
    filterKeywordBasedOnBrand(data, updateState(SCHEMA.PRODUCT_NAMES))
  }, [data])

  const uploadCSVModal = useModal(false)

  const handleUpload = (csv = '') => {
    const urls = csv.split(/[\r\n|\n|,]/).filter(Boolean)
    updateState(SCHEMA.URLS)([...data[SCHEMA.URLS], ...urls])
    uploadCSVModal.handleCloseModal()
  }

  const submit = () => {
    const payload = {
      ...data,
      [SCHEMA.COUNTRIES]: data[SCHEMA.COUNTRIES].value,
      status: OMM_STATUS_OPTIONS.SUBMITTED_FOR_PROCESSING,
    }

    createMediaMeasurementCampaign(payload)
      .then((item) => {
        // even that the audience is not created under draft,
        // logging as it was will help post analysis
        newLog({
          entity: 'online-media-measurements',
          data: {
            action: 'create draft',
            audienceName: item.name,
            audienceId: item._id,
          },
        }).then(() => {
          newLog({
            entity: 'online-media-measurements',
            data: {
              action: item.status,
              audienceName: item.name,
              audienceId: item._id,
            },
          })
        })
        sendAnalytics('create', 'media-measurement-campaigns', item)
        setStep(STEPS.FOUR)
      })
      .catch((error) => {
        toast.error(error.message)
      })
  }

  const handleClose = () => {
    onClose()
    refresh()
    setTimeout(() => {
      setStep(STEPS.ONE)
      setData(BASE_STATE)
    }, 400)
  }

  return (
    <section
      className="media-measurement-campaign-modal"
      style={{ display: isOpen ? 'block' : 'none' }}
    >
      <If condition={title}>
        <h1 className="st-new-entity-modal__title mb-8">{title}</h1>
      </If>
      <Choose>
        <When condition={step === STEPS.ONE}>
          <StepOne data={data} updateState={updateState} />
          <div className="mt-4 flex justify-between">
            <Button
              className="st-new-entity-modal__button"
              theme={BUTTON_THEME.SECONDARY}
              onClick={handleClose}
            >
              Exit
            </Button>
            <Button
              className="st-new-entity-modal__button"
              onClick={() => setStep(STEPS.TWO)}
              disabled={
                !(
                  data[SCHEMA.NAME] &&
                  data[SCHEMA.CAMPAIGN_OBJECTIVE] &&
                  data[SCHEMA.BRAND_NAME] &&
                  data[SCHEMA.COUNTRIES]?.value?.length &&
                  data[SCHEMA.LOOKBACK] &&
                  data[SCHEMA.COUNTRIES].destinationOptions.includes(
                    data[SCHEMA.DELIVERY_DESTINATION]
                  )
                )
              }
            >
              Add URLs
            </Button>
          </div>
        </When>
        <When condition={step === STEPS.TWO}>
          <StepTwo data={data} updateState={updateState} />
          <div className="mt-4 flex justify-between gap-4">
            <Button
              className="st-new-entity-modal__button"
              theme={BUTTON_THEME.SECONDARY}
              onClick={() => setStep(STEPS.ONE)}
            >
              Back
            </Button>
            <div className="flex gap-4">
              <Button
                className="st-new-entity-modal__button"
                theme={BUTTON_THEME.SECONDARY}
                onClick={uploadCSVModal.handleOpenModal}
              >
                Upload CSV
              </Button>
              <Button
                className="st-new-entity-modal__button"
                onClick={submit}
                disabled={!data[SCHEMA.URLS].length}
              >
                Submit
              </Button>
            </div>
          </div>
          <UploadCSVModal
            isOpen={uploadCSVModal.isOpen}
            handleClose={uploadCSVModal.handleCloseModal}
            onUpload={handleUpload}
            title=""
          />
        </When>
        {/* <When condition={step === STEPS.THREE}>
          <StepThree data={data} updateReports={updateReports} />
          <div className="mt-4 flex justify-between">
            <Button
              className="st-new-entity-modal__button"
              theme={BUTTON_THEME.SECONDARY}
              onClick={() => setStep(STEPS.TWO)}
            >
              Back
            </Button>
            <Button className="st-new-entity-modal__button" onClick={submit}>
              Submit
            </Button>
          </div>
        </When> */}
        <When condition={step === STEPS.FOUR}>
          <StepFour />
          <div className="mt-4 flex justify-center">
            <Button className="st-new-entity-modal__button" onClick={handleClose}>
              Got it
            </Button>
          </div>
        </When>
      </Choose>
    </section>
  )
}

CreateMediaMeasurementCampaignModal.propTypes = {
  title: PropTypes.string,
  onClose: PropTypes.func,
  refresh: PropTypes.func,
}

CreateMediaMeasurementCampaignModal.defaultProps = {
  title: 'New Audience',
  onClose: () => {},
  refresh: () => {},
}

export default CreateMediaMeasurementCampaignModal
